import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Copy, Share2, LogOut, Users, Crown, X } from "lucide-react";
import { useFb } from "../../utils/football";
import { toast } from "sonner";

const Row = ({ e }) => (
  <div className={`flex items-center justify-between rounded-lg px-3 py-2.5 ${e.is_current_user ? "border border-amber-400/40 bg-amber-400/10" : ""}`} data-testid={`league-standing-${e.rank}`}>
    <div className="flex min-w-0 items-center gap-3">
      <span className="w-6 text-center font-extrabold text-muted-foreground">{e.rank}</span>
      <div className="min-w-0">
        <div className="truncate text-sm font-bold">
          {e.display_name}
          {e.is_current_user && <span className="ml-1 text-xs wc-gold">(you)</span>}
          {e.manager_of_day_count > 0 && <Crown size={12} className="ml-1 inline text-amber-400" />}
        </div>
        {e.country && <div className="truncate text-xs text-muted-foreground">{e.country}</div>}
      </div>
    </div>
    <span className="font-extrabold wc-gold">{e.points} <span className="text-[10px] font-normal text-muted-foreground">pts</span></span>
  </div>
);

export default function FootballLeagueDetailPage() {
  const { id } = useParams();
  const fb = useFb();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fb.get(`/football/leaderboard/league/${id}`);
      setLeague(d.league);
      setEntries(d.entries || []);
    } catch { toast.error("Could not load league"); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => { navigator.clipboard?.writeText(league.code); toast.success("Invite code copied!"); };

  const share = async () => {
    const text = `Join my Football World Cup 2026 fantasy league "${league.name}" on APL! Use code ${league.code} to join.`;
    if (navigator.share) {
      try { await navigator.share({ title: league.name, text }); return; } catch { /* cancelled */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const doLeave = async () => {
    setLeaving(true);
    try {
      await fb.post(`/football/leagues/${id}/leave`, {});
      toast.success("You left the league");
      navigate("/football/leagues");
    } catch { toast.error("Could not leave league"); setLeaving(false); }
  };

  if (loading) {
    return (
      <div className="football-theme min-h-screen bg-background p-4" data-testid="league-detail-loading">
        <div className="h-32 animate-pulse rounded-xl bg-secondary/50" />
        <div className="mt-4 h-60 animate-pulse rounded-xl bg-secondary/50" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="football-theme min-h-screen bg-background p-6 text-center" data-testid="league-detail-missing">
        <p className="text-sm text-muted-foreground">League not found.</p>
        <button onClick={() => navigate("/football/leagues")} className="mt-3 rounded-xl wc-bg-gold px-5 py-2.5 text-sm font-extrabold text-slate-900">Back to Leagues</button>
      </div>
    );
  }

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="football-league-detail-page">
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/football/leagues")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="league-detail-back"><ChevronLeft size={18} /> My Leagues</button>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-extrabold text-white">{league.name}</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-white/80">
              <Users size={14} /> {league.member_count} member{league.member_count === 1 ? "" : "s"}
              <span className="opacity-50">·</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${league.is_private ? "bg-black/30 text-white" : "wc-bg-gold text-slate-900"}`}>{league.is_private ? "Private" : "Public"}</span>
            </div>
          </div>
          <button onClick={share} className="shrink-0 rounded-full bg-black/30 p-2.5 text-white" data-testid="league-share-btn" aria-label="share"><Share2 size={18} /></button>
        </div>
        <button onClick={copyCode} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-black/30 px-3 py-2 text-sm font-bold text-white" data-testid="league-copy-code">
          Invite code: <span className="font-mono wc-gold">{league.code}</span> <Copy size={14} />
        </button>
      </div>

      <div className="mx-auto max-w-md px-4">
        <h2 className="mb-2 mt-5 text-sm font-bold text-muted-foreground">Standings</h2>
        <div className="rounded-xl border border-border bg-card p-2">
          {entries.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="league-detail-empty">No members yet.</div>
          ) : (
            <div className="space-y-1">{entries.map((e) => <Row key={e.user_id} e={e} />)}</div>
          )}
        </div>

        <button onClick={() => setConfirmLeave(true)} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 py-3 text-sm font-bold text-red-400" data-testid="league-leave-btn">
          <LogOut size={16} /> Leave League
        </button>
      </div>

      {confirmLeave && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" data-testid="league-leave-confirm">
          <div className="football-theme w-full max-w-sm rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-extrabold">Leave this league?</h3>
              <button onClick={() => setConfirmLeave(false)} className="rounded-lg p-1 text-muted-foreground hover:bg-secondary" data-testid="league-leave-cancel"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-foreground">You will be removed from <span className="font-bold text-foreground">{league.name}</span>. {league.is_owner ? "Ownership passes to another member." : ""} You can rejoin later with the invite code.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setConfirmLeave(false)} className="flex-1 rounded-xl bg-secondary py-2.5 text-sm font-bold" data-testid="league-leave-cancel-btn">Cancel</button>
              <button onClick={doLeave} disabled={leaving} className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-extrabold text-white disabled:opacity-50" data-testid="league-leave-confirm-btn">{leaving ? "Leaving…" : "Leave"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
